// Graph Builder taxonomy — adventure creator following the Cybercore
// mission/op format (SitRep -> Mission -> Op -> Encounter, plus the Node
// Map, locations, hazards, and stakes that hang off them).
// Sources: content/gmg/missions/tactical-overview.md, missions.md,
// mission-design.md.

// Define the option type
export type SelectOption = {
	label: string;
	value: string;
	svg?: string;
};

// ─── Locations (Firestore-backed, user-editable) ───────────────────────
// Location categories (Space, Spaceship, Starbase, ...) are TYPES; their
// sub-locations are SUB TYPES. Stored in the `locations` collection so they
// can be added/edited/deleted in-app (seeded by scripts/seedGraphLocations.ts).
export interface LocationSubtype {
	label: string;
	value: string;
}
export interface GraphLocation {
	id: string; // slug, also the node `type` value
	name: string;
	subtypes: LocationSubtype[];
}

export const LOCATION_DEFAULT_SHAPE = 'square';

export const slugify = (s: string): string =>
	s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// Rich seed taxonomy; editable in-app afterwards.
export const SEED_LOCATIONS: GraphLocation[] = [
	{
		id: 'space', name: 'Space Location',
		subtypes: ['Open Space', 'Asteroid Field', 'Nebula', 'Debris Field', 'Wormhole', 'Jump Point', 'Derelict Drift', 'Minefield', 'Comet Trail', 'Dark Zone'].map((l) => ({ label: l, value: slugify(l) })),
	},
	{
		id: 'spaceship', name: 'Spaceship Location',
		subtypes: ['Bridge / CIC', 'Engineering', 'Cargo Bay', 'Hangar', 'Med Bay', 'Crew Quarters', 'Server Room', 'Airlock / EVA', 'Corridor', 'Reactor Core', 'Brig', 'Armoury', 'Life Support', 'Maintenance Crawlspace'].map((l) => ({ label: l, value: slugify(l) })),
	},
	{
		id: 'starbase', name: 'Starbase Location',
		subtypes: ['Command Deck', 'Docking Ring', 'Promenade', 'Reactor Section', 'Hydroponics', 'Security Hub', 'Comms Array', 'Cargo Manifold', 'Habitation Block', 'Detention Level'].map((l) => ({ label: l, value: slugify(l) })),
	},
	{
		id: 'outpost', name: 'Outpost Location',
		subtypes: ['Landing Pad', 'Control Shack', 'Generator Room', 'Storage Depot', 'Barracks', 'Watchtower', 'Comms Relay', 'Supply Cache', 'Perimeter Fence', 'Mess Hall'].map((l) => ({ label: l, value: slugify(l) })),
	},
	{
		id: 'city', name: 'City Location',
		subtypes: ['Spaceport', 'Corporate Tower', 'Undercity / Slums', 'Marketplace', 'Data Haven', 'Cantina', 'Transit Hub', 'Security Precinct', 'Black Clinic', 'Rooftops', 'Sewers / Maintenance', 'Arcology'].map((l) => ({ label: l, value: slugify(l) })),
	},
	{
		id: 'planetary', name: 'Planetary Location',
		subtypes: ['Surface', 'Cave System', 'Crater', 'Ancient Ruins', 'Research Station', 'Mining Site', 'Wilderness', 'Settlement', 'Orbital Elevator', 'Atmosphere Processor'].map((l) => ({ label: l, value: slugify(l) })),
	},
];

// Type icon, rendered as a badge in the bottom-right corner of the node shape.
export const type2SVG: { [key: string]: string } = {
	'mission': '/svg/graph/mission.svg',
	'sitrep': '/svg/graph/sitrep.svg',
	'op': '/svg/graph/op.svg',
	'encounter': '/svg/graph/encounter.svg',
	'rtc': '/svg/graph/rtc.svg',
	'location': '/svg/graph/location.svg',
	'node': '/svg/graph/node.svg',
	'npc': '/svg/graph/npc.svg',
	'hazard': '/svg/graph/hazard.svg',
	'reward': '/svg/graph/reward.svg',
	'tag': '/svg/graph/tag.svg',
};

export const getType2SVG = (type: string): string => {
	if (!type) {
		return '';
	}
	return type2SVG[type] || '/svg/graph/missing.svg';
};

// Default shape per type family. Applied when the type changes; the user can
// still override via the shape selector afterwards.
export const type2Shape: { [key: string]: string } = {
	'mission': 'hexagon',
	'sitrep': 'circle',
	'op': 'pentagon',
	'encounter': 'triangle',
	'rtc': 'octagon',
	'location': 'square',
	'node': 'diamond',
	'npc': 'circle',
	'hazard': 'triangle',
	'reward': 'diamond',
	'tag': 'circle',
};

export const optionTypes = (): SelectOption[] => {
	return [
		{ label: 'Mission', value: 'mission', svg: type2SVG['mission'] },
		{ label: 'SitRep', value: 'sitrep', svg: type2SVG['sitrep'] },
		{ label: 'Op', value: 'op', svg: type2SVG['op'] },
		{ label: 'Encounter', value: 'encounter', svg: type2SVG['encounter'] },
		{ label: 'RTC Event', value: 'rtc', svg: type2SVG['rtc'] },
		// Location categories are appended dynamically from the `locations`
		// Firestore table (see the store / GraphTreeBuilder type dropdown).
		{ label: 'Node (Hackable)', value: 'node', svg: type2SVG['node'] },
		{ label: 'NPC / Faction', value: 'npc', svg: type2SVG['npc'] },
		{ label: 'Hazard', value: 'hazard', svg: type2SVG['hazard'] },
		{ label: 'Reward / Stakes', value: 'reward', svg: type2SVG['reward'] },
		{ label: 'Tag', value: 'tag', svg: type2SVG['tag'] },
	];
};

export const optionSubType = (type: string): SelectOption[] => {
	const subtypes: Record<string, SelectOption[]> = {
		// The contract. One sentence objective, hidden complication.
		mission: [
			{ label: 'Retrieval', value: 'retrieval' },
			{ label: 'Elimination', value: 'elimination' },
			{ label: 'Sabotage', value: 'sabotage' },
			{ label: 'Rescue', value: 'rescue' },
			{ label: 'Escort', value: 'escort' },
			{ label: 'Infiltration', value: 'infiltration' },
			{ label: 'Investigation', value: 'investigation' },
			{ label: 'Salvage', value: 'salvage' },
			{ label: 'Defence', value: 'defence' },
			{ label: 'Heist', value: 'heist' },
			{ label: 'Survival', value: 'survival' },
		],
		// Scene-setting between Ops: roleplay, resupply, travel.
		sitrep: [
			{ label: 'Briefing', value: 'briefing' },
			{ label: 'Resupply', value: 'resupply' },
			{ label: 'Travel', value: 'travel' },
			{ label: 'Downtime', value: 'downtime' },
		],
		// The three-Op arc, plus off-book work.
		op: [
			{ label: 'Insertion (Op 1)', value: 'insertion' },
			{ label: 'Escalation (Op 2)', value: 'escalation' },
			{ label: 'Objective & Extraction (Op 3)', value: 'objective_extraction' },
			{ label: 'Side Op', value: 'side_op' },
			{ label: 'Deep Op', value: 'deep_op' },
		],
		// Each Op holds 2-3 of these. Combine for the memorable ones.
		encounter: [
			{ label: 'Combat', value: 'combat' },
			{ label: 'Skill', value: 'skill' },
			{ label: 'Environmental', value: 'environmental' },
			{ label: 'Social', value: 'social' },
			{ label: 'Discovery', value: 'discovery' },
			{ label: 'Ambush', value: 'ambush' },
		],
		// One per hour, every hour. Escalates across the three Ops.
		rtc: [
			{ label: 'Environmental Pressure', value: 'environmental_pressure' },
			{ label: 'Direct Threat', value: 'direct_threat' },
			{ label: 'Existential Stakes', value: 'existential_stakes' },
			{ label: 'Logistical', value: 'logistical' },
		],
		// Location categories + their sub-locations are Firestore-backed; see
		// the `locations` store/collection, not this hardcoded map.
		// Node Map types — exactly the Threat Band node types.
		node: [
			{ label: 'Access', value: 'access' },
			{ label: 'Sensor', value: 'sensor' },
			{ label: 'Control', value: 'control' },
			{ label: 'Weapon', value: 'weapon' },
			{ label: 'Server', value: 'server' },
		],
		// Who wants what, who gets spaced when those wants collide.
		npc: [
			{ label: 'Employer', value: 'employer' },
			{ label: 'Contact', value: 'contact' },
			{ label: 'Faction', value: 'faction' },
			{ label: 'Security', value: 'security' },
			{ label: 'Rival Squad', value: 'rival_squad' },
			{ label: 'Civilian', value: 'civilian' },
			{ label: 'Creature', value: 'creature' },
			{ label: 'Automaton', value: 'automaton' },
			{ label: 'Insider', value: 'insider' },
		],
		// Map legend states from mission-design.md.
		hazard: [
			{ label: 'Lighting', value: 'lighting' },
			{ label: 'Gravity', value: 'gravity' },
			{ label: 'Atmosphere', value: 'atmosphere' },
			{ label: 'Radiation', value: 'radiation' },
			{ label: 'Temperature', value: 'temperature' },
			{ label: 'Structural', value: 'structural' },
		],
		// What's at stake, either way.
		reward: [
			{ label: 'Payment', value: 'payment' },
			{ label: 'Loot', value: 'loot' },
			{ label: 'Intel', value: 'intel' },
			{ label: 'Reputation', value: 'reputation' },
			{ label: 'Consequence', value: 'consequence' },
		],
		tag: [
			{ label: 'Core', value: 'core' },
			{ label: 'Origin', value: 'origin' },
			{ label: 'Skill', value: 'skill' },
			{ label: 'Equipment', value: 'equipment' },
			{ label: 'Legacy', value: 'legacy' },
			{ label: 'Misc', value: 'misc' },
		],
	};
	// Alphabetical by label, so additions never need manual re-ordering.
	return (subtypes[type] ?? []).slice().sort((a, b) => a.label.localeCompare(b.label));
};

export const optionShape = (): SelectOption[] => {
	return [
		{ label: 'Circle', value: 'circle' },
		{ label: 'Triangle', value: 'triangle' },
		{ label: 'Square', value: 'square' },
		{ label: 'Diamond', value: 'diamond' },
		{ label: 'Pentagon', value: 'pentagon' },
		{ label: 'Hex', value: 'hexagon' },
		{ label: 'Octagon', value: 'octagon' },
	];
};

export const shape2Offset: { [key: string]: { x: number; y: number } } = {
	'circle': { x: 0.1, y: 0.5 },
	'triangle': { x: 0.1, y: 0.6 },
	'square': { x: 0.1, y: 0.5 },
	'diamond': { x: 0.1, y: 0.5 },
	'pentagon': { x: 0.1, y: 0.55 },
	'hexagon': { x: 0.1, y: 0.5 },
	'octagon': { x: 0.1, y: 0.5 },
};
